import { Controller } from "@hotwired/stimulus"

/**
 * Disclosure Controller for Progressive Disclosure Pattern
 *
 * Enhances HTML5 <details> elements with:
 * - Proper ARIA attribute management
 * - Smooth animations
 * - Keyboard shortcuts (optional)
 * - Analytics tracking (optional)
 *
 * Implements WCAG 2.1 AA disclosure pattern:
 * https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/
 *
 * Usage:
 *   <details data-controller="disclosure" data-disclosure-target="item">
 *     <summary data-action="click->disclosure#toggle">Question</summary>
 *     <div>Answer content</div>
 *   </details>
 *
 * @class DisclosureController
 * @extends Controller
 */
export default class extends Controller {
  static targets = ["item"]

  static values = {
    trackAnalytics: { type: Boolean, default: false },
    animationDuration: { type: Number, default: 300 } // ms
  }

  /**
   * Initialize controller
   */
  connect() {
    console.log("Disclosure controller connected")

    // Set initial ARIA attributes
    this.updateAriaAttributes()

    // Listen for toggle event
    this.element.addEventListener("toggle", this.handleToggle.bind(this))
  }

  /**
   * Cleanup on disconnect
   */
  disconnect() {
    this.element.removeEventListener("toggle", this.handleToggle.bind(this))
  }

  /**
   * Handle toggle action (called when summary is clicked)
   * @param {Event} event - Click event
   */
  toggle(event) {
    // The browser will handle the actual toggling
    // We just update ARIA attributes and track analytics
    this.updateAriaAttributes()

    // Track analytics if enabled
    if (this.trackAnalyticsValue) {
      this.trackDisclosureEvent()
    }
  }

  /**
   * Handle native <details> toggle event
   * @param {Event} event - Toggle event
   */
  handleToggle(event) {
    this.updateAriaAttributes()

    // Optional: Smooth scroll the opened item into view
    if (this.element.open) {
      setTimeout(() => {
        this.scrollIntoViewIfNeeded()
      }, this.animationDurationValue)
    }
  }

  /**
   * Update ARIA attributes to match current state
   */
  updateAriaAttributes() {
    const summary = this.element.querySelector("summary")
    const isOpen = this.element.open

    if (summary) {
      summary.setAttribute("aria-expanded", isOpen.toString())

      // Update icon aria-label if exists
      const icon = summary.querySelector(".disclosure-icon")
      if (icon) {
        icon.setAttribute(
          "aria-label",
          isOpen ? "Collapse" : "Expand"
        )
      }
    }
  }

  /**
   * Scroll the disclosure into view if it's outside viewport
   * (Prevents content from being hidden below fold when expanded)
   */
  scrollIntoViewIfNeeded() {
    const rect = this.element.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight

    // Check if bottom of element is below viewport
    if (rect.bottom > viewportHeight) {
      this.element.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      })
    }
  }

  /**
   * Track disclosure interaction for analytics
   * (e.g., Google Analytics, Plausible, etc.)
   */
  trackDisclosureEvent() {
    const questionText = this.element.querySelector("summary")?.textContent.trim()
    const action = this.element.open ? "expand" : "collapse"

    // Example: Google Analytics 4 event
    if (typeof gtag !== "undefined") {
      gtag("event", "disclosure_interaction", {
        event_category: "FAQ",
        event_label: questionText,
        action: action
      })
    }

    // Example: Plausible Analytics
    if (typeof plausible !== "undefined") {
      plausible("FAQ Interaction", {
        props: {
          question: questionText,
          action: action
        }
      })
    }

    // Log to console for debugging
    console.log(`Disclosure ${action}:`, questionText)
  }

  /**
   * Expand the disclosure (programmatically)
   */
  expand() {
    if (!this.element.open) {
      this.element.open = true
      this.updateAriaAttributes()
    }
  }

  /**
   * Collapse the disclosure (programmatically)
   */
  collapse() {
    if (this.element.open) {
      this.element.open = false
      this.updateAriaAttributes()
    }
  }

  /**
   * Toggle the disclosure (programmatically)
   */
  toggleState() {
    this.element.open = !this.element.open
    this.updateAriaAttributes()
  }
}
